<template>
    <!-- Mobile trigger. Desktop keeps the kebab in the sidebar top bar, so this button only
         ever paints inside Feed.vue's `lg:hidden` header. Pass :withTrigger="false" when the
         parent supplies its own button and drives the drawer through v-model. -->
    <button
        v-if="withTrigger"
        type="button"
        @click="toggle"
        :class="[
            'relative z-[52] w-8 h-8 rounded-lg flex items-center justify-center transition-all border active:scale-95',
            isOpen
                ? 'bg-white/15 text-white border-white/20'
                : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border-white/10 hover:border-white/20'
        ]"
        aria-label="Menu"
        :aria-expanded="isOpen"
    >
        <Menu class="w-4 h-4" />
    </button>

    <Teleport to="body">
        <!-- Stays mounted so the panel can slide. `lg:hidden` lives here because the teleport
             lands on <body>, outside the header wrapper that hides the trigger on desktop. -->
        <div class="lg:hidden" :inert="!isOpen">
            <!-- 1000+ band on purpose: the join cards' "BEST DEAL" ribbon escapes its parent at
                 z-index 100 and lives in shared code, so the backdrop clears it by an order of
                 magnitude rather than the ribbon being lowered for everyone. -->
            <Transition
                enter-active-class="transition-opacity duration-200 ease-out"
                enter-from-class="opacity-0"
                leave-active-class="transition-opacity duration-150 ease-in"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="isOpen"
                    class="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-md"
                    @click="close"
                />
            </Transition>

            <div
                class="fixed right-0 top-0 z-[1010] flex h-full w-[80%] sm:w-[476px] flex-col overflow-y-auto border-l border-white/10 bg-zinc-950 transition-transform duration-200 ease-out"
                :class="isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'"
                style="-webkit-overflow-scrolling: touch"
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
            >
                <!-- The trigger sits inside Feed.vue's `z-30` header, which is its own stacking
                     context, so it is blurred by this backdrop. The panel carries its own close
                     affordance instead of the header being hoisted above the panel. -->
                <button
                    ref="closeButtonRef"
                    type="button"
                    @click="close"
                    class="absolute right-4 top-4 z-[1020] flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/70 transition-all hover:bg-white/10 hover:text-white active:scale-95"
                    aria-label="Close menu"
                >
                    <X class="w-4 h-4" />
                </button>

                <div class="px-5 pb-10 pt-6">
                    <a :href="homeFeedUrl" aria-label="Back to clips feed" class="inline-flex items-center" @click="close">
                        <picture>
                            <source
                                srcset="https://sm.naughtycdn.com/images/newTour/nalogo-1x.webp 1x, https://sm.naughtycdn.com/images/newTour/nalogo-2x.webp 2x, https://sm.naughtycdn.com/images/newTour/nalogo-3x.webp 3x"
                                type="image/webp"
                            />
                            <img
                                src="https://sm.naughtycdn.com/images/newTour/nalogo-1x.png"
                                srcset="https://sm.naughtycdn.com/images/newTour/nalogo-1x.png 1x, https://sm.naughtycdn.com/images/newTour/nalogo-2x.png 2x, https://sm.naughtycdn.com/images/newTour/nalogo-3x.png 3x"
                                width="160"
                                height="24"
                                class="h-auto w-[160px]"
                                alt="Naughty America"
                            />
                        </picture>
                    </a>

                    <!-- Mirrors ClipsKebabMenu so the two menus never disagree. -->
                    <nav class="mt-8 space-y-1">
                        <template v-if="isUserLoggedIn">
                            <KebabMenuItem
                                :type="'a'"
                                :icon="Home"
                                label="Browse more Naughty America"
                                :href="homeUrl"
                                @click="close"
                            />
                            <KebabMenuItem
                                :type="'a'"
                                :icon="Settings"
                                label="Account Settings"
                                :href="route('members2.account.profile')"
                                @click="close"
                            />
                            <KebabMenuItem
                                :type="'a'"
                                :icon="HelpCircle"
                                label="Help & Support"
                                href="https://support.latouraineinc.com"
                                @click="close"
                            />

                            <!-- Deliberately leaves the drawer open: the confirmation is this row
                                 turning into "Reported", not a modal. Same contract as
                                 ClipsKebabMenu's entry. -->
                            <KebabMenuItem
                                v-if="canReport"
                                :icon="hasReported ? CheckCircle2 : Flag"
                                :label="reportLabel"
                                :variant="hasReported ? 'default' : 'danger'"
                                @click="reportClip"
                            />
                            <p v-if="reportError" class="px-3 pb-1 text-red-400 text-xs">
                                {{ reportError }}
                            </p>

                            <KebabMenuDivider />

                            <KebabMenuItem
                                :type="'a'"
                                :icon="LogOut"
                                label="Sign Out"
                                :href="route('members2.auth.logout')"
                                variant="danger"
                                @click="close"
                            />
                        </template>

                        <template v-else>
                            <KebabMenuItem
                                :type="'a'"
                                :icon="Home"
                                label="Browse more Naughty America"
                                :href="homeUrl"
                                @click="close"
                            />
                            <KebabMenuItem
                                :type="'a'"
                                :icon="User"
                                label="Sign In"
                                :href="route('members2.auth.login', { dest: currentUrl })"
                                @click="close"
                            />
                            <KebabMenuItem
                                :type="'a'"
                                :icon="HelpCircle"
                                label="Help & Support"
                                href="https://support.latouraineinc.com"
                                @click="close"
                            />
                        </template>
                    </nav>

                    <!-- A direct navigation, never a modal: the module's conversion path has no
                         modal on it. -->
                    <a
                        v-if="! isUserLoggedIn"
                        :href="joinUrl"
                        class="mt-8 flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 active:scale-[0.98]"
                        @click="handleJoinClick"
                    >
                        Become a Member
                    </a>

                    <!-- Inside the wrapper so it scrolls with the panel and picks up its
                         padding. Shown to guests and members alike - the desktop sidebar
                         carries the same block, and mobile has no other route to it. -->
                    <LegalFooterLinks />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { CheckCircle2, Flag, HelpCircle, Home, LogOut, Menu, Settings, User, X } from 'lucide-vue-next'
import KebabMenuItem from '@/Components/Reels/KebabMenuItem.vue'
import KebabMenuDivider from '@/Components/Reels/KebabMenuDivider.vue'
import LegalFooterLinks from '@/Components/Common/LegalFooterLinks.vue'
import { useClipAnalytics } from '@/composables/useClipAnalytics'
import { useClipReport } from '@/composables/useClipReport'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    // Drives every prefix-able route in here - www / tour / www3 / members2 - so the
    // menu keeps the visitor on the domain they arrived on.
    routePrefix: {
        type: String,
        default: 'www'
    },
    withTrigger: {
        type: Boolean,
        default: true
    },
    // True only on the /clips/sfw routes - keeps the drawer's own logo link inside
    // the sfw route family, matching Feed.vue's header logo convention.
    isSfwFeed: {
        type: Boolean,
        default: false
    },
    // The clip on screen, forwarded by Feed.vue, or null when the drawer is mounted without
    // clip context. Its id targets reports; its slug rides the join link as custom7 so
    // the sale is attributable to the clip, not just the placement.
    currentClip: {
        type: Object,
        default: null
    },
})

const emit = defineEmits(['update:modelValue'])

const { trackJoinCtaClicked } = useClipAnalytics({ routePrefix: props.routePrefix })

const isOpen = ref(props.modelValue)

const open = () => { isOpen.value = true }
const close = () => { isOpen.value = false }
const toggle = () => { isOpen.value = ! isOpen.value }

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search
    }

    return ''
})

const homeUrl = computed(() => route(props.routePrefix + '.main.home'))

// Logo click convention: back to the clips feed, same partition (sfw stays sfw) -
// matches Feed.vue's/ClipsSidebar.vue's homeFeedUrl. Distinct from homeUrl, which is
// the general site home used by the "Browse more Naughty America" item below.
const homeFeedUrl = computed(() =>
    route(props.routePrefix + '.verticalClips.' + (props.isSfwFeed ? 'sfw.' : '') + 'feed')
)

// Computed so the href follows the clip as the visitor swipes.
const joinUrl = computed(() => route('www.join.signUp', {
    custom6: 'clipsMobileMenuJoin',
    ...(props.currentClip?.slug ? { custom7: props.currentClip.slug } : {}),
}))

// Fires before the anchor's own navigation - the request is fire-and-forget and does not
// block it. Clip context comes from the currentClip prop (null until a clip mounts);
// `cta_source` tells the entry points apart.
const handleJoinClick = () => {
    trackJoinCtaClicked('clipsMobileMenuJoin', props.currentClip ?? null, joinUrl.value)
    close()
}

// The drawer outlives the clip - it stays mounted while the feed swipes - so the reported
// state is keyed on the clip inside the composable.
const {
    canReport,
    reportLabel,
    reportError,
    hasReported,
    reportClip,
} = useClipReport({
    clip: () => props.currentClip,
    routePrefix: props.routePrefix,
    isUserLoggedIn: () => props.isUserLoggedIn,
})

// Focus goes in on open and back to whatever opened the drawer on close; with only `inert`
// toggling, focus stayed on the trigger behind the backdrop.
const closeButtonRef = ref(null)
let returnFocusEl = null

const handleEscape = (event) => {
    if (event.key === 'Escape') {
        close()
    }
}

watch(() => props.modelValue, (value) => {
    isOpen.value = value
})

watch(isOpen, (value) => {
    if (value !== props.modelValue) {
        emit('update:modelValue', value)
    }

    if (value) {
        document.addEventListener('keydown', handleEscape)
        returnFocusEl = document.activeElement
        nextTick(() => closeButtonRef.value?.focus())
    } else {
        document.removeEventListener('keydown', handleEscape)

        if (returnFocusEl?.isConnected) {
            returnFocusEl.focus()
        }

        returnFocusEl = null
    }
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape)
})

// The parent may drive the drawer through v-model or through these, whichever suits
// the trigger it owns. Body overflow is deliberately untouched - Feed.vue owns it.
defineExpose({ open, close, toggle, isOpen })
</script>
